import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { SEARCH_LOCATION_PARAMS } from '@components/store-locator/configs';
import { StoreLocator } from '@components/store-locator/models';
import { StoreLocatorService } from '@components/store-locator/store-locator.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { MapComponent } from '@shared/components/map';
import { SearchComponent } from '@shared/components/search';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { LabelKey, StoreWithUuid } from '@shared/models';
import { ConfiguratorLinkService } from '@shared/services/configurator-link';
import { GtmEvent } from '@shared/services/data-layer/models';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { LocalitySearch } from '@shared/services/locality-search/models';
import { LogsService } from '@shared/services/logs';
import { UrlParamsService } from '@shared/services/url-params';
import { CommonStore, FunnelStore, StoreLocatorStore } from '@stores';
import { take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-store-locator',
  imports: [TitleComponent, SearchComponent, MapComponent, ButtonComponent, TranslatePipe, SvgComponent],
  templateUrl: './store-locator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreLocatorComponent extends AbstractDynamicComponent<StoreLocator> implements OnInit {
  readonly storeListEl = viewChild<ElementRef<HTMLDivElement>>('storeList');
  readonly searchLocationParams = SEARCH_LOCATION_PARAMS;
  readonly locations = signal<LocalitySearch[]>(null!);
  readonly locationsOptions = signal<LabelKey[]>(null!);
  readonly isStoreCardListVisible = signal(false);
  readonly hasAppointmentCta = inject(StoreLocatorStore).select('hasAppointmentCta');
  protected readonly gtmEvent = computed<Partial<GtmEvent>>(() => {
    const gtm = this.gtmConfigSelectStoreButton();
    return {
      event: gtm?.event,
      group: gtm?.group,
      description: gtm?.description,
      context: gtm?.context,
      event_type: 'search',
    };
  });
  private readonly storeLocatorStore = inject(StoreLocatorStore);
  readonly storesListDisplayed = this.storeLocatorStore.select('listDisplayed');
  readonly gtmConfigSelectStoreButton = this.storeLocatorStore.select('gtmConfigSelectStoreButton');
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly appointmentFunnel = inject(CommonStore).select('appointmentFunnel');
  private readonly window = inject(DOCUMENT).defaultView as Window;
  private readonly searchLocation = this.urlParamsService.param(SEARCH_LOCATION_PARAMS);
  private readonly storeLocatorService = inject(StoreLocatorService);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly logs = inject(LogsService);
  private readonly router = inject(Router);
  private readonly funnelStore = inject(FunnelStore);
  private readonly configuratorLink = inject(ConfiguratorLinkService);

  constructor() {
    super();
    effect(() => {
      const searchLocation = this.searchLocation();
      const storeSelected = untracked(this.storeLocatorStore.select('mapStoreSelected'));
      if (searchLocation) {
        this.logs.debug('StoreLocatorComponent', `Search: ${searchLocation}`);
        this.storeLocatorService
          .getLocation(searchLocation ?? '')
          .pipe(
            take(1),
            tap((locations) => {
              if (locations && locations.length > 0) {
                this.logs.trace('StoreLocatorComponent', `Received ${locations.length} locations`, locations);
                this.locations.set(locations);
                this.locationsOptions.set(
                  locations.reduce<LabelKey[]>((acc, location) => {
                    acc.push({ label: location.name, key: location.id });
                    return acc;
                  }, [])
                );
              } else {
                this.locations.set([]);
                this.locationsOptions.set([]);
                this.logs.warn('StoreLocatorComponent', 'No locations found');
              }
            })
          )
          .subscribe();
      } else if (storeSelected) {
        this.storeLocatorStore.batchUpdate({ delete: ['mapStoreSelected', 'listDisplayed'] });
      }
    });
  }

  ngOnInit(): void {
    this.configuratorLink.captureFromUrl();
    this.funnelStore.update('currentStep', 1);
    this.storeLocatorStore.update('gtmConfigSelectStoreButton', this.data.gtmConfigSelectStoreButton);
    this.logs.debug('StoreLocatorComponent', 'Get All Stores');
    if (this.data.hasAppointmentCta) {
      this.storeLocatorStore.update('hasAppointmentCta', this.data.hasAppointmentCta);
    }
    this.elasticSearchService
      .getAllStores()
      .pipe(take(1))
      .subscribe({ error: (err) => this.logs.error('StoreLocatorComponent', 'getAllStores failed', err) });
  }

  onOptionSelected(locationsSelected: LabelKey) {
    const location = this.locations().find((location) => location.id === locationsSelected.key);
    if (!location) return;
    this.logs.info('StoreLocatorComponent', `Location selected: ${location?.name}`, location);
    this.elasticSearchService
      .getStoresByLocation(location)
      .pipe(
        take(1),
        tap((stores) => {
          if (stores.length > 0) {
            this.storeLocatorStore.update('mapStoreSelected', stores[0]);
          }
          this.isStoreCardListVisible.set(true);
          this.scrollToStoreList();
        })
      )
      .subscribe({ error: (err) => this.logs.error('StoreLocatorComponent', 'getStoresByLocation failed', err) });
  }

  onStoreAppointmentClick(store: StoreWithUuid) {
    const url = this.appointmentFunnel()?.urls?.step2;
    if (url) {
      this.funnelStore.batchUpdate({
        update: {
          validatedStep: 1,
          storeSelected: store,
        },
        delete: ['slotSelected'],
      });
      void this.router.navigate([url]);
    }
  }

  private scrollToStoreList(): void {
    this.window.setTimeout(() => {
      const elRef = this.storeListEl();
      if (!elRef) {
        return;
      }
      elRef.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 300);
  }
}
